import { useMemo } from "react";
import dayjs from "dayjs";
import { ConfigParameterDTO } from "api";
import { useGetAllowedValues, useGetConfigParameter, useSites, useUtilityBillsYears } from "hooks";
import { ALL_MONTHS } from "consts";

export const useMissingDocumentsReferenceData = () => {
  const { data: configParameter } = useGetConfigParameter();
  const { data: availableYears } = useUtilityBillsYears();
  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();

  const allMonths = useMemo(() => [...ALL_MONTHS], []);

  const companyMonths = useMemo(
    () =>
      configParameter
        ?.filter((param: ConfigParameterDTO) =>
          param.name.includes("company_month"),
        )
        .map((param: ConfigParameterDTO) => param.configValue) ?? [
        "Jan",
        "Dec",
      ],
    [configParameter],
  );

  const hasCompanyFiscalConfig = useMemo(
    () =>
      Array.isArray(configParameter) &&
      configParameter.some((param: ConfigParameterDTO) =>
        param.name.includes("company_month"),
      ),
    [configParameter],
  );

  const allFiscalYears: string[] = useMemo(
    () =>
      (availableYears &&
        availableYears.map((fy: number) => `${fy} Fiscal Year`)) || [
        `${dayjs().get("year")} Fiscal Year`,
      ],
    [availableYears],
  );

  const countries = useMemo(
    () =>
      allowedValues
        ? allowedValues.countries.sort().map((coun: string) => ({
            name: coun.replaceAll("_", " "),
            label: coun,
            value: coun,
          }))
        : [],
    [allowedValues],
  );

  const utilityTypes = useMemo(
    () =>
      allowedValues
        ? allowedValues.utilityTypes
            .sort((utA: any, utB: any) => (utA.name > utB.name ? 1 : -1))
            .map((ut: any) => ({ name: ut.name, label: ut.name, value: ut.id }))
        : [],
    [allowedValues],
  );

  const sites = useMemo(
    () =>
      sitesData
        ? sitesData.map((site: any) => ({
            name: site.name,
            label: site.name,
            value: site.id,
          }))
        : [],
    [sitesData],
  );

  const businessUnits = useMemo(
    () =>
      allowedValues
        ? allowedValues.businessUnits
            .sort()
            .map((bu: string) => ({ name: bu, label: bu, value: bu }))
        : [],
    [allowedValues],
  );

  const countryRegions = useMemo(
    () =>
      allowedValues
        ? allowedValues.countryRegions
            .sort()
            .map((cr: string) => ({ name: cr, label: cr, value: cr }))
        : [],
    [allowedValues],
  );

  const sitesByCountry = useMemo(
    () =>
      sitesData
        ? sitesData.reduce((acc: any, site: any) => {
            if (!acc[site.country]) {
              acc[site.country] = [];
            }
            acc[site.country].push(site.id);
            return acc;
          }, [])
        : [],
    [sitesData],
  );

  return {
    availableYears,
    companyMonths,
    hasCompanyFiscalConfig,
    allFiscalYears,
    countries,
    utilityTypes,
    sites,
    businessUnits,
    countryRegions,
    sitesByCountry,
    allMonths,
  };
};
